import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import { ProductImageMissingVisualConfig } from '@epicuro-next/ataraxia/product-image';
import { plural } from '@epicuro-next/platform/i18n';
import { PrepackView } from '@epicuro-next/state/config-strip-state';
import { COLLAPSED, EXPANDED } from '@epicuro-next/ui';
import { DabData, ScaleSets } from 'graphql-api/gql-api-interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NeverOutOfStockIconSize } from '../never-out-of-stock-icon/interfaces/never-out-of-stock-icon-size.enum';

const BREAKPOINT = '(max-width: 769px)';

@Component({
  selector: 'epc-pre-pack',
  templateUrl: './pre-pack.component.html',
  styleUrls: ['./pre-pack.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrePackComponent implements OnDestroy, OnInit {
  public NeverOutOfStockIconSize = NeverOutOfStockIconSize;
  public imageMaxSizes = IMAGE_MAX_SIZES;

  @Input()
  public prepack: PrepackView;

  @Input()
  public maxAvailableQuantity = 0;

  @Input()
  public quantityWarningThreshold = 5;

  @Input()
  public numberOfImagesToDisplay = 5;

  /** scale sets for the materials */
  @Input()
  public scaleSets: ScaleSets;
  @Input()
  public selectedScale: string;
  @Input()
  public dabsData: DabData;

  @Input()
  public selectedVariant: string;

  @Input()
  public opened = false;

  @Input()
  public showAddNote = true;

  @Input()
  public set quantity(value: number) {
    if (value === undefined || value === this.quantityControlValue) {
      return;
    }

    this.quantityControl.setValue(value, { emitEvent: false, onlySelf: true });
  }

  @Input() public note: string;

  @Input()
  public titleAttributes: string[];

  @Input() public differentMaterialDimensions = false;

  @Input()
  public productImageMissingVisualConfig?: ProductImageMissingVisualConfig;

  @Output()
  public quantityChanged = new EventEmitter<number>();

  @Output()
  public noteChanged = new EventEmitter<string>();

  @Output()
  public clickedMaterialImage = new EventEmitter<string>();

  @Output()
  public prepackSelected = new EventEmitter<string>();

  private destroyed$ = new Subject<void>();

  constructor(private breakpointObserver: BreakpointObserver) {}

  public quantityControl = new UntypedFormControl(0);
  public noteControl = new UntypedFormControl('');

  public inboundTitle = $localize`:ConfigStrip|Label for inbound quantity badge@@inboundQuantityBadgeLabel:Inbound quantity`;

  public get totalItemsInPrepactText() {
    return plural(
      this.totalItemsInPrepack,
      {
        1: $localize`:ConfigStrip|prepack items count@@prepacItemsCountOne:${this.totalItemsInPrepack} item`,
      },
      $localize`:ConfigStrip|prepack items count@@prepacItemsCountMore:${this.totalItemsInPrepack} items`,
    );
  }

  public get lowInvetoryWarningText() {
    return plural(
      this.quantityLeft,
      {
        0: $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningZero:No units available`,
        1: $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningOne:${this.quantityLeft} unit left`,
      },
      $localize`:ConfigStrip|inbound quantity warning@@inboundQuantityWarningMore:${this.quantityLeft} units left`,
    );
  }

  public ngOnInit(): void {
    this.quantityControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((quantity) => {
        this.quantityChanged.emit(quantity);
      });
    this.noteControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((note) => {
        this.noteChanged.emit(note);
      });
  }

  public get quantityControlValue() {
    return +this.quantityControl.value;
  }

  public get noteControlValue() {
    return this.noteControl.value;
  }

  public get collapseState() {
    return this.opened ? EXPANDED : COLLAPSED;
  }

  public get totalItemsInPrepack() {
    return this.prepack.quantities.reduce((acc, prepackUnit) => {
      return acc + prepackUnit.quantity;
    }, 0);
  }

  public get quantityLeft() {
    return this.maxAvailableQuantity - this.quantityControlValue;
  }

  public get noMoreAvailable() {
    return this.quantityControlValue >= this.maxAvailableQuantity;
  }

  public get deliveryItems() {
    return this.prepack.quantities.reduce(
      (acc, prepackUnit) => {
        return {
          items: {
            ...acc.items,
            [prepackUnit.productId]: { quantity: prepackUnit.quantity },
          },
        };
      },
      { items: {} },
    );
  }

  public get inboundQuantity() {
    return this.dabsData?.inboundItemsQuantity?.[0] ?? 0;
  }

  public get thumbnailsVisible() {
    const isSmallDevice = this.breakpointObserver.isMatched(BREAKPOINT);
    if (isSmallDevice) return this.prepack.variants?.length === 1;
    return true;
  }

  public get discount() {
    return this.prepack.priceRanges?.[0]?.discount;
  }

  public get discountVisible() {
    return (this.discount?.max ?? 0) > 0;
  }

  public togglePrePack() {
    this.opened = !this.opened;
    this.prepackChanged(
      this.prepack.productId,
      this.prepack.variants[0]?.productId,
    );
  }

  public clickMaterialImage(variantId: string, event?: Event) {
    event?.stopPropagation();
    this.clickedMaterialImage.emit(variantId);
  }

  public onQuantityChange() {
    const variantIds = this.prepack.variants.map(
      (variant) => variant.productId,
    );
    if (!variantIds.includes(this.selectedVariant)) {
      this.prepackChanged(this.prepack.productId, variantIds[0]);
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private prepackChanged(prepackId: string, selectedVariantId: string) {
    this.prepackSelected.emit(prepackId);
    this.clickedMaterialImage.emit(selectedVariantId);
  }
}
